import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    OnInit,
    HostListener
} from '@angular/core';
import { SpinnerService } from '../../spinner/spinner.service';
import { MatDialog } from '@angular/material/dialog';
import { FileTooLargeWarningDialogComponent } from './file-too-large-warning-dialog/file-too-large-warning-dialog.component';

/**
 * This component allows the user to upload a file
 * @input fileString is the base 64 string of the file
 * @input fileType is the type of the file, such as "image/png"
 * @input fileExtension is the extension of the file, such as .png
 * @input fileName is the name of the file, including the extension, such as myImage.png
 * @input showFileName is a boolean that controls if the file name shows up as a label beside the button. It is true by default.
 * @input buttonLabel is a label that shows up on the button. It is 'BROWSE' by default.
 * @input disabled is whether the control should be disabled. It is false by default;
 * @input required is whether the control is required. This just puts a red start at the end of a blank file name.
 * @input readAsText is whether this should read the data as text or base64 data. Defaults to false;
 * @input accepts is a string to tell the file upload what file types are accepted
 * @input deleteable is a boolean that determines if the browse becomes delete when a file is present. Defaults to true.
 * @input deleteLabel is the label that shows on the delete button. Defaults to 'DELETE'
 * @input width is a string that sets the width of the component. Defaults to 400px.
 *
 * It provides you with the following
 * @output fileStringChange is an event that fires the fileString when it changes
 * @output fileTypeChange is an event that fires the fileType when it changes
 * @output fileExtensionChange is an event that fires the fileExtension when it changes
 * @output fileNameChange is an event that fires the fileName when it changes
 * @output changes is an event that fires whenever any variable changes
 *
 * Note: because each input has an output "changes," you can use a two-way binding, e.g. [(fileString)]="myString"
 * Note: even though this provides @input accepts, you must check the type in case of custom error messages
 */
@Component({
    selector: 'add-file',
    templateUrl: './add-file.component.html',
    styleUrls: [
        './add-file.component.css'
    ]
})
export class AddFileComponent implements OnInit {

    // Interface Properties
    @Input() fileString: string | ArrayBuffer;
    @Output() fileStringChange = new EventEmitter<string | ArrayBuffer>();

    @Input() fileType: string;
    @Output() fileTypeChange = new EventEmitter<string>();

    @Input() fileExtension: string;
    @Output() fileExtensionChange = new EventEmitter<string>();

    @Input() fileName: string;
    @Output() fileNameChange = new EventEmitter<string>();

    @Input() fileSize: number;
    @Output() fileSizeChange = new EventEmitter<number>();

    @Input() showFileName = true;
    @Input() deletable = true;
    @Input() deleteLabel = 'DELETE';
    @Input() buttonLabel = 'BROWSE';
    @Input() disabled = false;
    @Input() required = false;
    @Input() hideRequiredAsterisk = false;
    @Input() readAsText = false;
    @Input() accepts: string;
    @Input() width = '400px';
    @Input() maxFileSize = 10485760;
    @Input() maxFileSizeMessage = '';
    @Output() changes = new EventEmitter();

    // Local Properties
    @ViewChild('fileInput', { static: true }) fileInputRef: ElementRef;
    @ViewChild('browseButton', { read: ElementRef }) browseButton: ElementRef;
    @ViewChild('deleteButton', { read: ElementRef }) deleteButton: ElementRef;
    hoverTarget;
    droppable: boolean;
    hovering = false;

    // Class Functions
    constructor(
        private spinnerService: SpinnerService,
        private dialog: MatDialog
    ) { }

    // Event Functions
    ngOnInit() {
        // POST: initializes the component
        const div = document.createElement('div');
        this.droppable = ('draggable' in div)
            || ('ondragstart' in div && 'ondrop' in div);
    }

    @HostListener('dragover', ['$event'])
    public onDragOver(evt) {
        if (this.droppable) {
            evt.preventDefault();
            evt.stopPropagation();
            this.hoverTarget = event.target;
            this.hovering = true;
        }
    }

    @HostListener('dragleave', ['$event'])
    public onDragLeave(evt) {
        if (this.droppable) {
            evt.preventDefault();
            evt.stopPropagation();
            this.hovering = false;
        }
    }

    @HostListener('drop', ['$event'])
    public onDrop(evt) {
        if (this.droppable) {
            evt.preventDefault();
            evt.stopPropagation();
            const files = evt.dataTransfer.files;
            if (files.length > 0) {
                this.hovering = false;
                this.setFile(files[files.length - 1]);
            }
        }
    }

    chooseFile() {
        // PRE: the choose file button is clicked
        // POST: opens the file selection
        this.fileInputRef.nativeElement.dispatchEvent(new MouseEvent('click'));
    }

    delete() {
        // PRE: the delete button is clicked
        // POST: clears the content of that attachment
        this.fileString = null;
        this.fileStringChange.emit(this.fileString);
        this.fileType = null;
        this.fileTypeChange.emit(this.fileType);
        this.fileExtension = null;
        this.fileExtensionChange.emit(this.fileExtension);
        this.fileName = null;
        this.fileNameChange.emit(this.fileName);
        this.fileInputRef.nativeElement.value = '';
        this.fileSize = 0;
        this.fileSizeChange.emit(0);
        this.changes.emit();
    }

    // Methods
    setFile(file: File) {
        // PRE: a file uploaded from <input type="file">
        // POST: sets up the file data

        // A file must be present
        if (!file) {
            return;
        }

        // The file must be no larger than 10 mb
        if (file.size > this.maxFileSize) {
            this.dialog.open(FileTooLargeWarningDialogComponent, {
                disableClose: true,
                data: { maxFileSizeMessage: this.maxFileSizeMessage }
            });
            return;
        }
        const reader = new FileReader();
        this.fileName = file.name;
        this.fileNameChange.emit(this.fileName);
        this.fileType = file.type;
        this.fileTypeChange.emit(this.fileType);
        this.fileExtension = file.name.split('.').pop();
        this.fileExtensionChange.emit(this.fileExtension);
        this.spinnerService.showSpinner = true;
        setTimeout(() => {
            if (this.readAsText) {
                reader.onload = (e) => {
                    this.fileString = reader['result'];
                    this.fileStringChange.emit(this.fileString);
                    this.changes.emit();
                    this.spinnerService.showSpinner = false;
                };
                reader.readAsText(file);
            } else {
                reader.onload = (e) => {
                    this.fileTypeChange.emit(this.fileType);
                    // const base64String = e.target['result'].match(/^data:.+\/(.+);base64,(\S*)$/);	// This takes a lot of time
                    const splitStrings = e.target['result'].toString().split(',');
                    if (splitStrings.length == 2) {
                        this.fileString = splitStrings[1];
                    } else {
                        this.fileString = e.target['result'];
                    }
                    this.fileStringChange.emit(this.fileString);
                    this.fileSize = file.size;
                    this.fileSizeChange.emit(file.size);
                    this.changes.emit();
                    this.spinnerService.showSpinner = false;
                };
                reader.readAsDataURL(file);
            }
        }, 20);
    }
}
